import React, {useCallback, useMemo, useState} from 'react';
import {Controller, useWatch} from 'react-hook-form';
import {FormError, FormLabel, getInputWrapperClass, getWholeDivWrapperClass} from '../sharedStyles.tsx';
import {AlertCircle, ImageIcon, Loader, RefreshCw, Upload, X} from 'lucide-react';
import {CUSTOM_ERROR_TYPE, FormComponentProps, ImageUploadComponent, ValidationRuleLevel} from '../../DynamicFormTypes.ts';
import {FormImageType, ImageSubmission} from "../../../../api/ApiTypes.ts";
import {useFormMethods, useFormStatic} from "../../DyanmicFormContext.tsx";
import {useFileApi} from "../../../../context/ApiContext.tsx";
import {ImageDisplay} from "./ImageDisplay.tsx";
import {useEventListener} from "../../../../context/EventBus.ts";
import {getImageTypeDisplay, setRuleType} from "../../dyanimcFormUtil.tsx";
import {useValidationRules} from "../../hooks/useValidationRules.tsx";

interface UploadingFile {
    id: string;
    file: File;
    error?: string;
}

function extractSubmission(data: any): ImageSubmission | undefined {
    if (Array.isArray(data) && data.length > 0) {
        return data[0] as ImageSubmission;
    }
    return undefined;
}

const ImageUploadRenderer: React.FC<FormComponentProps<ImageUploadComponent>> = ({ component }) => {

    const { formMethods } = useFormMethods()
    const { metaData, formPermissions } = useFormStatic();
    const fileApi = useFileApi();

    // Watch the form value for this field - this is now our single source of truth
    const uploadedImage = useWatch({
        control: formMethods.control,
        name: component.fieldKey,
        defaultValue: []
    });

    const uploadedImageUi = useMemo(()=> {
        return extractSubmission(uploadedImage);
    }, [uploadedImage])

    // Local state only for UI concerns that don't need to persist
    const [uploadingFile, setUploadingFile] = useState<UploadingFile>();
    const [componentError, setComponentError] = useState<string | null>(null);
    const [updatingTypeId, setUpdatingTypeId] = useState<number | null>(null);
    const [showWarning, setShowWarning] = useState(false);

    // Helper function to update the form value
    const setUploadedImage = useCallback((newValue: ImageSubmission | undefined) => {
        if (!formPermissions.canEdit) {
            console.log("Edits or auto set values not allowed, image update aborted for", component.fieldKey);
            return;
        }

        //when we actually set the value, set it as an array
        const newValueConverted: ImageSubmission[] = newValue ? [newValue] : [];
        formMethods.setValue(component.fieldKey, newValueConverted);

        if (newValue !== null && newValue !== undefined && newValue.imageType !== component.requiredType) {
            formMethods.setError(component.fieldKey, {
                type: setRuleType(ValidationRuleLevel.ERROR, CUSTOM_ERROR_TYPE.MANUAL),
                message: `Missing required image types: ${getImageTypeDisplay(component.requiredType)}`
            });
        } else {
            formMethods.clearErrors(component.fieldKey);
        }
    }, [component.fieldKey, component.requiredType, formMethods]);

    useEventListener("IMAGE_RETRIEVED_FROM_COMPARABLE", async (event: {
        index: number;
        dataGridKey: string;
        imageUrl?: string | undefined;
    }) => {
        try {

            let myPersonalKey = component.fieldKey;
            if (!myPersonalKey.includes(".")) {
                return;
            }

            if (!event.dataGridKey || event.index === undefined || event.index === null || !event.imageUrl) {
                return;
            }

            myPersonalKey = myPersonalKey.split(".").pop() || "";
            const newKey = `${event.dataGridKey}.${event.index}.${myPersonalKey}`;


            if (newKey !== component.fieldKey) {
                return;
            }


            const uploadedImage = await fileApi.uploadFormImageFromUrl({
                formSubmissionId: metaData.submissionId,
                imageType: component.requiredType ?? FormImageType.NONE,
                fieldKey: component.fieldKey,
                externalImageUrl: event.imageUrl
            });

            setUploadedImage(uploadedImage);

        }
        catch (e) {
            console.error(`[${event}] ERROR:`, e);
        }
    });

    const uploadFile = useCallback(async (fileToUpload: File) => {
        if (!formPermissions.canEdit) {
            setComponentError("You do not have permission to upload images.");
            return;
        }
        const tempId = `uploading-${Date.now()}-${fileToUpload.name}`;
        const newUploadingFile: UploadingFile = { id: tempId, file: fileToUpload };
        setUploadingFile(newUploadingFile);

        try {
            const uploadedImage = await fileApi.uploadFormImage({
                file: fileToUpload,
                formMeta: metaData,
                imageType: component.requiredType ?? FormImageType.NONE,
                fieldKey: component.fieldKey,
                externalImageUrl: undefined
            });

            setUploadedImage(uploadedImage);
        } catch (error) {
            console.error(`Failed to upload ${fileToUpload.name}:`, error);
            setUploadingFile(prev =>
                prev?.id === tempId ? { ...prev, error: 'Upload failed' } : prev
            );
        } finally {
            setUploadingFile(undefined);
        }
    }, [metaData, component.fieldKey, fileApi, setUploadedImage, formPermissions.canEdit]);

    const handleFileSelection = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files) return;
        const files = Array.from(event.target.files);
        if (event.target) event.target.value = '';

        files.forEach(file => {
            if (!file.type.startsWith('image/')) {
                setComponentError(`${file.name} is not a valid image file.`);
                return;
            }
            uploadFile(file);
        });
    }, [uploadFile]);

    const handleRemoveUploadedImage = useCallback(async () => {
        try {
            if (!formPermissions.canEdit) {
                setComponentError("You do not have permission to remove images.");
                return;
            }

            if (component.fieldKey.endsWith("ProsCons")) {
                const imageUrlKey = component.fieldKey.replace(/ProsCons$/, "ImageUrl");
                formMethods.setValue(imageUrlKey, null);
                formMethods.clearErrors(imageUrlKey);
            }

            setUploadedImage(undefined);
        } catch (error) {
            console.error('Image removal failed:', error);
            setComponentError('Failed to remove image');
        }
    }, [fileApi, setUploadedImage, formPermissions.canEdit]);

    const handleTypeUpdate = useCallback(async (imageId: number, newType: FormImageType) => {
        if (!formPermissions.canEdit) {
            setComponentError("You do not have permission to update image types.");
            return;
        }
        setUpdatingTypeId(imageId);
        setComponentError(null);
        try {
            const updatedImage = await fileApi.updateFormImage({
                formSubmissionId: metaData.submissionId,
                generalFileId: imageId,
                imageType: newType
            });

            setUploadedImage(updatedImage);
        } catch (e) {
            console.error('Failed to update image type:', e);
            setComponentError('Failed to update image type. Please try again.');
        } finally {
            setUpdatingTypeId(null);
        }
    }, [metaData.submissionId, fileApi, setUploadedImage]);

    const removeUploadingFile = () => {
        setUploadingFile(undefined);
    };

    const validate = useValidationRules({ component });

    return (
        <Controller
            name={component.fieldKey}
            control={formMethods.control}
            rules={validate}
            defaultValue={null}
            render={({ fieldState }) => {
                if (component.compactUI) {
                    return (
                        <div className={getWholeDivWrapperClass(component) + ' mb-2 size-full'}>
                            <div className={getInputWrapperClass(component)}>
                                <div className="space-y-4">
                                    {componentError && (
                                        <div className="p-2 bg-red-50 border border-red-200 rounded-md flex items-center text-sm">
                                            <AlertCircle className="h-4 w-4 text-red-500 mr-2 flex-shrink-0"/>
                                            <p className="text-red-700">{componentError}</p>
                                        </div>
                                    )}

                                    {/* Main Carousel and Controls */}
                                    <div className="relative w-full aspect-[4/3] bg-gray-100 rounded overflow-hidden border border-gray-200">
                                        {uploadedImageUi ? (
                                            <>
                                                <div className="relative bg-gray-200" style={{ paddingTop: '75%' }}>
                                                    <ImageDisplay image={uploadedImageUi} />

                                                    <button type="button" onPointerEnter={() => setShowWarning(true)} onPointerLeave={() => setShowWarning(false)} onClick={() => handleRemoveUploadedImage()} className="absolute top-2 right-2 bg-black/50 hover:bg-red-500 text-white p-1.5 rounded-full transition-colors">{showWarning ? 'Delete Image' : <X className="h-3 w-3" />}</button>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <label
                                                    className="size-full text-center flex flex-col justify-center items-center border-2 border-dashed border-gray-300 rounded-md cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-all"
                                                >
                                                    <p className="text-md text-gray-500">No images.</p>
                                                    <ImageIcon className="w-full h-1/2 text-gray-300"/>
                                                    <Upload className="h-10 w-10 text-blue-500"/>
                                                    <p className="text-sm font-medium text-blue-600">Choose Image to Upload</p>
                                                    <input id={component.fieldKey} type="file" accept="image/*"
                                                           className="hidden" onChange={handleFileSelection}/>
                                                </label>
                                                {/* Uploading Files List */}
                                                {uploadingFile && (
                                                    <div className="space-y-2 p-3 border rounded-md">
                                                        <p className="text-sm font-medium text-gray-600">Uploading...</p>
                                                        <div key={uploadingFile.id}
                                                             className="flex items-center justify-between text-sm">
                                                            <p className={`truncate pr-4 ${uploadingFile.error ? 'text-red-600' : 'text-gray-700'}`}>{uploadingFile.file.name}</p>
                                                            {uploadingFile.error ? (
                                                                <div className='flex items-center gap-2'>
                                                                    <span
                                                                        className='text-red-500 text-xs'>{uploadingFile.error}</span>
                                                                    <button type="button"
                                                                            onClick={(e) => {
                                                                                uploadFile(uploadingFile.file)
                                                                                e.stopPropagation()
                                                                            }}
                                                                            className="text-blue-500 hover:text-blue-700">
                                                                        <RefreshCw className="h-4 w-4"/></button>
                                                                    <button type="button"
                                                                            onClick={() => removeUploadingFile()}
                                                                            className="text-gray-400 hover:text-gray-600">
                                                                        <X className="h-4 w-4 z-10"/></button>
                                                                </div>
                                                            ) : (
                                                                <Loader className="animate-spin h-4 w-4 text-blue-500"/>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                                <FormError
                                    fieldState={fieldState}
                                    component={component}
                                />
                            </div>
                        </div>
                    );
                }

                return (
                    <div className={getWholeDivWrapperClass(component)}>
                        <FormLabel component={component} />

                        <div className={getInputWrapperClass(component)}>
                            <div className="space-y-4">

                                {componentError && (
                                    <div className="p-2 bg-red-50 border border-red-200 rounded-md flex items-center text-sm">
                                        <AlertCircle className="h-4 w-4 text-red-500 mr-2 flex-shrink-0" />
                                        <p className="text-red-700">{componentError}</p>
                                    </div>
                                )}

                                {/* Main Carousel and Controls */}
                                <div className="bg-gray-50 rounded-lg overflow-hidden border border-gray-200">
                                    {uploadedImageUi ? (
                                        <>
                                            <div className="relative bg-gray-200" style={{ paddingTop: '75%' }}>
                                                <ImageDisplay image={uploadedImageUi} />

                                                <button type="button" onClick={() => handleRemoveUploadedImage()} className="absolute top-2 right-2 bg-black/50 hover:bg-red-500 text-white p-1.5 rounded-full transition-colors"><X className="h-3 w-3" /></button>
                                            </div>

                                            {/* Edit Section */}
                                            <div className="p-3 bg-white border-t space-y-2">
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                                        Image Type
                                                    </label>
                                                    <div className="flex items-center gap-2">
                                                        <select
                                                            value={uploadedImageUi?.imageType || FormImageType.NONE}
                                                            onChange={(e) => handleTypeUpdate(uploadedImageUi.generalFileId, e.target.value as FormImageType)}
                                                            disabled={updatingTypeId === uploadedImageUi.generalFileId}
                                                            className="block w-full text-sm rounded border-gray-300 focus:border-blue-500 focus:ring-blue-500 disabled:bg-gray-100"
                                                        >
                                                            <option value={FormImageType.NONE}>None</option>
                                                            <option key={component.requiredType} value={component.requiredType}>{component.requiredType}</option>
                                                        </select>
                                                        {updatingTypeId === uploadedImageUi.generalFileId && <Loader className="animate-spin h-4 w-4 text-blue-500" />}
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="p-8 text-center">
                                            <ImageIcon className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                                            <p className="text-sm text-gray-500">No image uploaded</p>
                                            <p className="text-xs text-gray-400 mt-1">Select image to begin.</p>
                                            <label className="block">
                                                <div className="border-2 border-dashed border-gray-300 rounded-md p-3 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-all">
                                                    <div className="flex items-center justify-center gap-2">
                                                        <Upload className="h-4 w-4 text-blue-500" />
                                                        <span className="text-sm font-medium text-blue-600">Choose Image to Upload</span>
                                                    </div>
                                                </div>
                                                <input id={component.fieldKey} type="file" accept="image/*" className="hidden" onChange={handleFileSelection} />
                                            </label>
                                        </div>
                                    )}
                                </div>

                                {/* Uploading Files List */}
                                {uploadingFile && (
                                    <div className="space-y-2 p-3 border rounded-md">
                                        <p className="text-sm font-medium text-gray-600">Uploading...</p>
                                        <div key={uploadingFile.id}
                                             className="flex items-center justify-between text-sm">
                                            <p className={`truncate pr-4 ${uploadingFile.error ? 'text-red-600' : 'text-gray-700'}`}>{uploadingFile.file.name}</p>
                                            {uploadingFile.error ? (
                                                <div className='flex items-center gap-2'>
                                                    <span className='text-red-500 text-xs'>{uploadingFile.error}</span>
                                                    <button type="button" onClick={() => uploadFile(uploadingFile.file)}
                                                            className="text-blue-500 hover:text-blue-700"><RefreshCw
                                                        className="h-4 w-4"/></button>
                                                    <button type="button"
                                                            onClick={() => removeUploadingFile()}
                                                            className="text-gray-400 hover:text-gray-600"><X
                                                        className="h-4 w-4"/></button>
                                                </div>
                                            ) : (
                                                <Loader className="animate-spin h-4 w-4 text-blue-500"/>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <FormError
                            fieldState={fieldState}
                            component={component}
                        />
                    </div>
                );
            }}
        />
    );
}
export const ImageUpload = React.memo(ImageUploadRenderer);